import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {

  private settings: Settings | null = null;
  private settingsSubject = new BehaviorSubject<Settings | null>(null);
  private unsubscribeSubject = new BehaviorSubject<UnsubscribeModel | null>(null);
  private managerUnsubscribeSubject = new BehaviorSubject<UnsubscribeModel | null>(null);

  public settings$ = this.settingsSubject.asObservable();
  public unsubscribeObject$ = this.unsubscribeSubject.asObservable();
  public managerUnsubscribeObject$ = this.managerUnsubscribeSubject.asObservable();

  constructor(private http: HttpClient, private router: Router) {
    if (this.router.url.includes('settings')) {
      this.getSettings();
    }
  }

  async getSettings(): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.http.get<Settings>('/api/Settings/GetSettings')
      );

      if (response) {
        this.settings = response;
        this.settingsSubject.next(response);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }

  async getUnsubscribe(token: string): Promise<UnsubscribeModel> {
    return await lastValueFrom(
        this.http.get<UnsubscribeModel>('/api/Unsubscribe/GetUnsubscribe', { params: { token } })
      );
  }

  async getManagerUnsubscribe(token: string): Promise<UnsubscribeModel> {
    return await lastValueFrom(
      this.http.get<UnsubscribeModel>('/api/Unsubscribe/GetManagerUnsubscribe', { params: { token } })
    );
  }

  updateSettings(data: SettingsUpdate | UnsubscribeModel): Observable<UpdateSettingsResponse> {
    return this.http.post<UpdateSettingsResponse>(
      '/api/Settings/UpdateSettings',
      data
    );
  }

  updateUnsubscribe(token: string, reason: string): Observable<UnsubscribeModel> {
    return this.http.post<UnsubscribeModel>(
      '/api/Unsubscribe/Unsubscribe',
      null,
      { params: { token, reason } }
    );
  }

  updateUnsubscribeSettings(data: UnsubscribeModel, token: string): Observable<UnsubscribeModel> {
    return this.http.post<UnsubscribeModel>(
      '/api/Unsubscribe/UpdateUnsubscribeSettings',
      data,
      { params: { token } }
    );
  }

  updateManagerUnsubscribe(data: UnsubscribeModel, token: string): Observable<UnsubscribeModel> {
    return this.http.post<UnsubscribeModel>(
      '/api/Unsubscribe/ManagerUnsubscribe',
      data,
      { params: { invitationCode: token } }
    );
  }

  recallTos(token: string): Observable<void> {
    return this.http.post<void>(
      '/api/Unsubscribe/UnsubscribeRecallToS',
      null,
      { params: { token } }
    );
  }

  resendTos(token: string): Observable<void> {
    return this.http.post<void>(
      '/api/Unsubscribe/UnsubscribeResendToS',
      null,
      { params: { token } }
    );
  }
}

export interface Settings {
  employeeId: number;
  notifyByEmail?: boolean | null;
  notifyByPush?: boolean | null;
  notifyBySMS?: boolean | null;
  notifyByEmailEnabled: boolean;
  notifyBySMSEnabled: boolean;
  notifyByPushEnabled: boolean;
  nextPushNotificationDate?: string | null;
  nextSMSNotificationDate?: string | null;
  nextEmailNotificationDate?: string | null;
  changeLanguageEnabled: boolean;
  languageId: number;
  languages: LanguageOptions[];
  fullname: string;
}

export interface UnsubscribeModel {
  employeeId: number;
  languageId: number;
  companyName: string;
  contactPerson: string;
  notifyByEmail: boolean;
  notifyBySMS: boolean;
  notifyByPush: boolean;
  notifyByEmailEnabled?: boolean | null;
  notifyBySMSEnabled?: boolean | null;
  notifyByPushEnabled?: boolean | null;
  firstname: string;
  lastname: string;
  nextPushNotificationDate: string | null;
  nextEmailNotificationDate: string | null;
  nextSMSNotificationDate: string | null;
  labels: { [key: string]: string } | Map<string, string>;
  tosContentMarkdown: string;
}

export interface LanguageOptions {
  id: number;
  englishName: string;
  code: string;
}

export type NotificationPreferences = Pick<Settings,
  'notifyByEmail' | 'notifyBySMS' | 'notifyByPush'>;

export interface SettingsUpdate extends NotificationPreferences {
  userLanguage: string;
}

export type UpdateSettingsResponse  = Pick<Settings,
  'nextPushNotificationDate' | 'nextSMSNotificationDate' | 'nextEmailNotificationDate'>;
